import { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";

const SECTION_LINKS = [
  { label: "Services", hash: "services" },
  { label: "Technology", hash: "technology" },
  { label: "Work", hash: "portfolio" },
  { label: "Process", hash: "process" },
  { label: "About", hash: "about" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const onHome = pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const buildLinks = () => {
    const base = SECTION_LINKS.map((l) => ({
      label: l.label,
      href: onHome ? `#${l.hash}` : `/#${l.hash}`,
    }));
    base.push({ label: "Careers", href: "/careers" });
    return base;
  };
  const LINKS = buildLinks();

  const ctaHref = onHome ? "#contact" : "/#contact";

  return (
    <header
      data-testid="site-nav"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-[#080A0F]/75 border-b border-white/[0.06]" : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <a href="/" data-testid="nav-logo" className="flex items-center gap-2.5 group">
          <span className="w-2.5 h-2.5 bg-[color:var(--accent)] rounded-full group-hover:scale-125 transition-transform" />
          <span className="font-display text-[19px] md:text-[22px] tracking-tight leading-none">
            Beacon<span className="text-[color:var(--accent)]">.</span>
            <span className="font-mono text-[12px] md:text-[13px] tracking-[0.18em] uppercase ml-2 text-white/70 align-middle">IT Solution</span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-7">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="nav-link mono-label !text-white/80 hover:!text-white transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <a
          href={ctaHref}
          data-testid="nav-cta"
          className="hidden md:inline-flex items-center gap-2 border border-white/15 hover:border-[color:var(--accent)] hover:bg-[color:var(--accent)] transition-colors px-4 py-2 text-[13px] font-medium"
        >
          Start a project <ArrowUpRight size={14} />
        </a>

        <button
          className="md:hidden text-white p-2"
          data-testid="nav-hamburger"
          aria-label="Menu"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="md:hidden border-t border-white/[0.06] bg-[#080A0F]/95 backdrop-blur-xl"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`nav-link-${l.label.toLowerCase()}`}
                  onClick={() => setOpen(false)}
                  className="font-display text-2xl tracking-tight hover:text-[color:var(--accent)]"
                >
                  {l.label}
                </a>
              ))}
              <a
                href={ctaHref}
                onClick={() => setOpen(false)}
                className="mt-2 inline-flex items-center gap-2 bg-[color:var(--accent)] px-4 py-3 w-fit"
              >
                Start a project <ArrowUpRight size={16} />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
